import {FormattedMessage} from 'react-intl';
import React, {useEffect, useState} from 'react';
import moment, {Moment} from 'moment';
import {graphql} from 'react-relay';
import CreateBulkFixedPriceLockMutation from '../../mutations/create_bulk_fixed_price_lock_mutation';
import Util from '../../forecast-app/shared/util/util';
import {getNextAvailableLock} from '../../components/new-ui/project/project-budget-v3/util/PeriodLocks';
import {FlexRow, Modal, SubHeading} from '@forecast-it/design-system';
import {useForecastFetchQuery} from '../../forecast-app/shared/hooks/useForecastFetchQuery';
import {RevenueMonthLockModalDeliverableRevenueQuery} from './__generated__/RevenueMonthLockModalDeliverableRevenueQuery.graphql';

export type RevenueMonthLockModalProps = {
	closeModal: () => void;
	project: {
		id: string;
		fixedPriceLocks: {
			edges: {
				node: {id: string; startDate: Moment; endDate: Moment; locked: boolean};
			}[];
		};
	};
	month: string;
	lockedAmounts: {
		total: {startDate: Moment; amount: number}[];
		time: {startDate: Moment; amount: number}[];
	};
};

// Fetched imperatively (not through the page's own query boundary) because it's only needed
// once, right before confirming a lock - see useForecastFetchQuery's usage note in AGENTS.md.
const deliverableRevenueQuery = graphql`
	query RevenueMonthLockModalDeliverableRevenueQuery(
		$projectId: ID!
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
	) {
		node(id: $projectId) {
			... on ProjectType {
				groupedFinancialNumbers(
					groupBy: ["PROJECT", "DELIVERABLE"]
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
				) {
					deliverableId
					recognitionLockedRevenue
					recognitionOpenRevenue
				}
			}
		}
	}
`;

const formatDateISO = (date: moment.Moment) => (date ? date.format('YYYY-MM-DD') : '');
export const RevenueMonthLockModal: React.FC<RevenueMonthLockModalProps> = ({closeModal, project, month, lockedAmounts}) => {
	const nextAvailableLock = getNextAvailableLock(project);

	const [saving, setSaving] = useState(false);
	const {fetch: fetchDeliverableRevenue, data: deliverableRevenueData} = useForecastFetchQuery<
		RevenueMonthLockModalDeliverableRevenueQuery['response'],
		Record<string, unknown>
	>(deliverableRevenueQuery);

	useEffect(() => {
		if (month) {
			const selectedPeriod = moment(month);
			fetchDeliverableRevenue({
				projectId: project.id,
				startYear: selectedPeriod.clone().startOf('month').year(),
				startMonth: selectedPeriod.clone().startOf('month').month() + 1,
				startDay: selectedPeriod.clone().startOf('month').date(),
				endYear: selectedPeriod.clone().endOf('month').year(),
				endMonth: selectedPeriod.clone().endOf('month').month() + 1,
				endDay: selectedPeriod.clone().endOf('month').date(),
			});
		}
		// Intentionally fetch once, on mount, using the month/project this modal was opened for.
	}, []);

	const onSave = () => {
		if (month && nextAvailableLock) {
			setSaving(true);
			const selectedPeriod = moment(month);

			const totalLockedAmount =
				lockedAmounts.total.find(
					lockedAmount => lockedAmount.startDate.format('YYYY-MM') === selectedPeriod.format('YYYY-MM')
				)?.amount || 0;
			const timeLockedAmount =
				lockedAmounts.time.find(
					lockedAmount => lockedAmount.startDate.format('YYYY-MM') === selectedPeriod.format('YYYY-MM')
				)?.amount || 0;
			// Each Deliverable's revenue, exactly as already fetched and computed above while the
			// period is still genuinely unlocked - passed straight through rather than having
			// service-company re-fetch it from financial-service after the lock row is written,
			// which can never see a Deliverable's true pre-lock revenue (see
			// docs/spec/billing/006-fp-deliverables-and-revenue-locking.md).
			const deliverableRevenues: {deliverableId: number; revenue: number}[] = [];
			for (const row of deliverableRevenueData?.node?.groupedFinancialNumbers ?? []) {
				if (row && row.deliverableId != null) {
					deliverableRevenues.push({
						deliverableId: row.deliverableId,
						revenue: (row.recognitionLockedRevenue ?? 0) + (row.recognitionOpenRevenue ?? 0),
					});
				}
			}
			const locks = [
				{
					startDate: formatDateISO(selectedPeriod.clone().startOf('month')),
					endDate: formatDateISO(selectedPeriod.clone().endOf('month')),
					locked: true,
					amount: totalLockedAmount,
					timeAmount: timeLockedAmount,
					deliverableRevenues,
				},
			];
			Util.CommitMutation(CreateBulkFixedPriceLockMutation, {projectId: project.id, locks: locks}, closeModal);
		}
	};

	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				<FormattedMessage id="fixed_price_lock.lock_modal_button" />
			</Modal.Title>
			<Modal.Content>
				<FlexRow>
					<SubHeading>
						<FormattedMessage
							id={'fixed_price_lock.lock_message'}
							values={{
								month: moment(month, 'YYYY-MM').format('MMMM YYYY'),
							}}
						/>
					</SubHeading>
				</FlexRow>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={closeModal} dataCy={'modal-btn-rename-report'}>
					<FormattedMessage id={'common.cancel'} />
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={onSave} disabled={saving}>
					<FormattedMessage id={'fixed_price_lock.lock_button'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
